import type { Rng } from '../core/rng';

/**
 * Pure music-theory helpers for the generative score: scales, Roman-numeral
 * chords, voice leading and melodic note choice. Pitches are MIDI numbers;
 * pitch classes are 0..11 (C = 0).
 */

export const SCALES = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
  phrygianDominant: [0, 1, 4, 5, 7, 8, 10],
  pentatonicMajor: [0, 2, 4, 7, 9],
  pentatonicMinor: [0, 3, 5, 7, 10],
  hungarianMinor: [0, 2, 3, 6, 7, 8, 11],
} as const satisfies Record<string, readonly number[]>;
export type ScaleName = keyof typeof SCALES;

export const CHORD_QUALITIES = {
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  pow: [0, 7],
  maj7: [0, 4, 7, 11],
  min7: [0, 3, 7, 10],
  dom7: [0, 4, 7, 10],
  add9: [0, 4, 7, 2],
  madd9: [0, 3, 7, 2],
} as const satisfies Record<string, readonly number[]>;
export type ChordQuality = keyof typeof CHORD_QUALITIES;

export interface Chord {
  symbol: string;
  /** Pitch class of the chord root. */
  root: number;
  quality: ChordQuality;
  /** Pitch classes, root first. */
  pcs: number[];
}

export const pc = (midi: number): number => ((Math.round(midi) % 12) + 12) % 12;

export function scalePcs(tonic: number, scale: ScaleName): number[] {
  return SCALES[scale].map((s) => (tonic + s) % 12);
}

const NUMERALS: Record<string, number> = { i: 0, ii: 2, iii: 4, iv: 5, v: 7, vi: 9, vii: 11 };
const ROMAN_RE = /^([b#]?)(VII|VI|V|IV|III|II|I|vii|vi|v|iv|iii|ii|i)(.*)$/;

/**
 * Parses a Roman numeral relative to `tonic` (a pitch class). Numerals count
 * major-scale degrees: `bVI` is a major chord on the minor sixth, `iv` a minor
 * chord on the fourth. Case picks major/minor; suffixes: `o` dim, `+` aug,
 * `7` (dominant for upper case, minor-seventh for lower), `maj7`, `sus2`,
 * `sus4`, `5` (power chord), `add9`.
 */
export function parseRoman(symbol: string, tonic: number): Chord {
  const m = ROMAN_RE.exec(symbol.trim());
  if (!m) throw new Error(`Bad chord symbol "${symbol}"`);
  const [, acc = '', num = 'I', suffix = ''] = m;
  const upper = num === num.toUpperCase();
  let semis = NUMERALS[num.toLowerCase()] ?? 0;
  if (acc === 'b') semis -= 1;
  else if (acc === '#') semis += 1;
  let quality: ChordQuality = upper ? 'maj' : 'min';
  if (suffix === 'o' || suffix === '°') quality = 'dim';
  else if (suffix === '+') quality = 'aug';
  else if (suffix === '7') quality = upper ? 'dom7' : 'min7';
  else if (suffix === 'maj7') quality = 'maj7';
  else if (suffix === 'sus2') quality = 'sus2';
  else if (suffix === 'sus4') quality = 'sus4';
  else if (suffix === '5') quality = 'pow';
  else if (suffix === 'add9') quality = upper ? 'add9' : 'madd9';
  else if (suffix !== '') throw new Error(`Bad chord suffix "${suffix}" in "${symbol}"`);
  const root = (((tonic + semis) % 12) + 12) % 12;
  return { symbol, root, quality, pcs: CHORD_QUALITIES[quality].map((i) => (root + i) % 12) };
}

/** Every MIDI note in [lo, hi] whose pitch class is in `pcs`, ascending. */
export function notesInRange(pcs: readonly number[], lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let n = Math.ceil(lo); n <= hi; n++) if (pcs.includes(pc(n))) out.push(n);
  return out;
}

/** Nearest note to `midi` inside [lo, hi] with a pitch class from `pcs`. */
export function nearestInSet(midi: number, pcs: readonly number[], lo: number, hi: number): number {
  let best = Math.round(Math.min(hi, Math.max(lo, midi)));
  let bestD = Infinity;
  for (const n of notesInRange(pcs, lo, hi)) {
    const d = Math.abs(n - midi);
    if (d < bestD || (d === bestD && n < best)) {
      best = n;
      bestD = d;
    }
  }
  return best;
}

/** Lowest note in [lo, hi] with pitch class `p` (falls back to octave folding). */
export function lowestOf(p: number, lo: number, hi: number): number {
  for (let n = Math.ceil(lo); n <= hi; n++) if (pc(n) === p) return n;
  return Math.ceil(lo) + ((p - pc(lo) + 12) % 12);
}

/**
 * Close-position voicing of `pcs` inside [lo, hi] that moves least from the
 * previous voicing (sum of absolute semitone motion between sorted voices).
 * Without a previous voicing the one centred nearest the range middle wins.
 */
export function voiceLead(
  prev: readonly number[] | null,
  pcs: readonly number[],
  lo: number,
  hi: number,
): number[] {
  const cands: number[][] = [];
  const n = pcs.length;
  for (let rot = 0; rot < n; rot++) {
    const order = [...pcs.slice(rot), ...pcs.slice(0, rot)];
    for (let base = lowestOf(order[0]!, lo, hi); base <= hi; base += 12) {
      const notes = [base];
      for (let k = 1; k < n; k++) {
        let next = notes[k - 1]! + 1;
        while (pc(next) !== order[k]) next++;
        notes.push(next);
      }
      if (notes[n - 1]! <= hi) cands.push(notes);
    }
  }
  if (!cands.length) {
    // Range too narrow for a close voicing: fold each pitch class into range.
    return pcs.map((p) => nearestInSet((lo + hi) / 2, [p], lo - 12, hi + 12)).sort((a, b) => a - b);
  }
  const mean = (a: readonly number[]): number => a.reduce((s, v) => s + v, 0) / a.length;
  const mid = (lo + hi) / 2;
  let best = cands[0]!;
  let bestCost = Infinity;
  for (const c of cands) {
    let cost: number;
    if (prev && prev.length) {
      const p = [...prev].sort((a, b) => a - b);
      if (p.length === c.length) {
        cost = 0;
        for (let i = 0; i < c.length; i++) cost += Math.abs(c[i]! - p[i]!);
      } else {
        cost = Math.abs(mean(c) - mean(p)) * c.length;
      }
      cost += Math.abs(mean(c) - mid) * 0.05;
    } else {
      cost = Math.abs(mean(c) - mid);
    }
    if (cost < bestCost - 1e-9) {
      best = c;
      bestCost = cost;
    }
  }
  return best;
}

/** Moves `midi` by `steps` degrees of the scale (diatonic transposition). */
export function stepInScale(midi: number, steps: number, tonic: number, scale: ScaleName): number {
  const pcs = scalePcs(tonic, scale);
  let n = Math.round(midi);
  // Snap onto the scale first.
  if (!pcs.includes(pc(n))) n = nearestInSet(n, pcs, n - 2, n + 2);
  const dir = Math.sign(steps);
  for (let k = 0; k < Math.abs(steps); k++) {
    n += dir;
    while (!pcs.includes(pc(n))) n += dir;
  }
  return n;
}

/**
 * Pitch classes a melody may use over a chord: chord tones plus scale tones
 * that are not a semitone away from a chord tone (the classic "avoid notes").
 */
export function melodyPcs(chordPcs: readonly number[], scale: readonly number[]): number[] {
  const out = new Set<number>(chordPcs);
  for (const s of scale) {
    if (out.has(s)) continue;
    const clash = chordPcs.some((c) => (s - c + 12) % 12 === 1 || (c - s + 12) % 12 === 1);
    if (!clash) out.add(s);
  }
  return [...out];
}

export interface MelodyChoice {
  prev: number;
  /** Signed interval of the previous melodic step (for gap-fill). */
  lastInterval: number;
  chordPcs: readonly number[];
  allowedPcs: readonly number[];
  lo: number;
  hi: number;
  /** Strong beats land on chord tones. */
  strong: boolean;
}

/**
 * Weighted choice of the next melody note: prefers small steps, chord tones,
 * avoids repeats and big leaps, and fills gaps after leaps (moves back the
 * other way).
 */
export function chooseMelodyNote(rng: Rng, c: MelodyChoice): number {
  const pool = notesInRange(c.strong ? c.chordPcs : c.allowedPcs, c.lo, c.hi);
  if (!pool.length) return Math.round(Math.min(c.hi, Math.max(c.lo, c.prev)));
  const weights = pool.map((n) => {
    const iv = n - c.prev;
    const a = Math.abs(iv);
    let w = Math.exp(-a / 2.6);
    if (a === 0) w *= 0.3;
    if (a > 7) w *= 0.15;
    if (c.chordPcs.includes(pc(n))) w *= 1.35;
    if (Math.abs(c.lastInterval) > 4 && Math.sign(iv) === Math.sign(c.lastInterval)) w *= 0.25;
    return w;
  });
  const total = weights.reduce((s, w) => s + w, 0);
  let roll = rng.next() * total;
  for (let i = 0; i < pool.length; i++) {
    roll -= weights[i]!;
    if (roll < 0) return pool[i]!;
  }
  return pool[pool.length - 1]!;
}
